import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '@/config/api';
import {
  Users,
  Filter,
  Inbox,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';

import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Skeleton } from '@/Components/ui/skeleton';
import { Label } from '@/Components/ui/label';
import { Input } from '@/Components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/Components/ui/tabs';
import { cn } from '@/lib/utils';

import RecruiterNavbar from './RecruiterNavbar';
import CandidateCard from './ApplicationCandidateCard';
import { useRecruiterAuth } from '../../../CompanyAdmin/Context/RecruiterAuthContext';

const TABS = ['All', 'Hired', 'Selected', 'Shortlisted', 'Submitted', 'Rejected'];
const PER_PAGE = 10;

export function ApplicationsPage() {
  const { recruiter } = useRecruiterAuth();

  const [allCandidates, setAllCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('All');
  const [jobFilter, setJobFilter] = useState('all');
  const [resumeMin, setResumeMin] = useState('');
  const [resumeMax, setResumeMax] = useState('');
  const [interviewFilter, setInterviewFilter] = useState('all');
  const [page, setPage] = useState(1);

  // ----- fetch -------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const fetchCandidates = async () => {
      if (!recruiter?.email) return;

      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ recruiterEmail: recruiter.email });
        const res = await fetch(
          `${API_BASE_URL}/api/recruiter/all-candidates?${params}`,
          { credentials: 'include' },
        );
        if (!res.ok) throw new Error('Failed to fetch candidates');
        const data = await res.json();
        if (!cancelled) setAllCandidates(data);
      } catch (e) {
        console.error('Error fetching candidates:', e);
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchCandidates();
    return () => {
      cancelled = true;
    };
  }, [recruiter?.email]);

  // ----- derived ----------------------------------------------------------
  const jobOptions = useMemo(() => {
    const set = new Set(allCandidates.map((c) => c.jobTitle).filter(Boolean));
    return ['all', ...[...set]];
  }, [allCandidates]);

  const filtered = useMemo(() => {
    let items = allCandidates;
    if (activeTab !== 'All') {
      items = items.filter(
        (c) => (c.status || '').toLowerCase() === activeTab.toLowerCase(),
      );
    }
    if (jobFilter !== 'all') {
      items = items.filter((c) => c.jobTitle === jobFilter);
    }
    const min = Number(resumeMin || 0);
    const max = resumeMax ? Number(resumeMax) : 999;
    if (resumeMin || resumeMax) {
      items = items.filter((c) => {
        const s = Number(c.Resume_Score ?? 0);
        return !isNaN(s) && s >= min && s <= max;
      });
    }
    if (interviewFilter === 'yes') {
      items = items.filter((c) => c.isInterviewScheduled === true);
    } else if (interviewFilter === 'no') {
      items = items.filter((c) => c.isInterviewScheduled === false);
    }
    return items;
  }, [activeTab, jobFilter, resumeMin, resumeMax, interviewFilter, allCandidates]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filtered.slice(start, start + PER_PAGE);
  }, [filtered, page]);

  // Reset page on filter changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, jobFilter, resumeMin, resumeMax, interviewFilter]);

  const tabCount = (tab) =>
    tab === 'All'
      ? allCandidates.length
      : allCandidates.filter(
          (c) => (c.status || '').toLowerCase() === tab.toLowerCase(),
        ).length;

  const gotoPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
    window.scrollTo({ top: 200, behavior: 'smooth' });
  };

  const resetFilters = () => {
    setJobFilter('all');
    setResumeMin('');
    setResumeMax('');
    setInterviewFilter('all');
  };

  // ----- error state ------------------------------------------------------
  if (error) {
    return (
      <>
        <RecruiterNavbar />
        <div className="mx-auto flex w-full max-w-7xl items-center justify-center px-4 py-16">
          <Card className="w-full max-w-md border-destructive/30">
            <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h2 className="text-lg font-semibold">Error loading applications</h2>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // ----- render ----------------------------------------------------------
  return (
    <>
      <RecruiterNavbar />

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Applications</h1>
            <p className="text-sm text-muted-foreground">
              Manage candidates across jobs — filter, view scores and shortlist
              quickly.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <HeaderStat
              icon={Users}
              value={loading ? '—' : allCandidates.length}
              label="Total"
            />
            <HeaderStat
              icon={Filter}
              value={loading ? '—' : filtered.length}
              label="Filtered"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
            {TABS.map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                className={cn(
                  'h-9 gap-2 rounded-md border border-border/40 bg-background px-3 text-sm',
                  'data-[state=active]:border-primary data-[state=active]:bg-primary/10',
                  'data-[state=active]:text-primary data-[state=active]:shadow-none',
                )}
              >
                <span>{t}</span>
                <Badge
                  variant="outline"
                  className={cn(
                    'h-5 border-0 px-1.5 text-[10px] font-medium',
                    activeTab === t
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {loading ? '—' : tabCount(t)}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Main grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_280px]">
          {/* List */}
          <Card className="border border-border/40 shadow-sm">
            <div className="flex items-center justify-between border-b border-border/40 px-6 py-4">
              <h3 className="text-base font-semibold">Candidate List</h3>
              <span className="text-xs text-muted-foreground">
                {loading ? 'Loading...' : `${filtered.length} results`}
              </span>
            </div>

            <CardContent className="space-y-3 px-4 py-4">
              {loading ? (
                <>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full rounded-md" />
                  ))}
                </>
              ) : paginated.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <Inbox className="h-10 w-10 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">
                    No candidates found.
                  </p>
                </div>
              ) : (
                paginated.map((c) => (
                  <CandidateCard
                    key={c.id}
                    candidate={c}
                    onViewProfile={() => alert(`View profile: ${c.name}`)}
                  />
                ))
              )}
            </CardContent>

            {!loading && filtered.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/40 px-4 py-3">
                <span className="text-xs text-muted-foreground">
                  Showing {(page - 1) * PER_PAGE + 1}–
                  {Math.min(page * PER_PAGE, filtered.length)} of{' '}
                  {filtered.length}
                </span>

                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => gotoPage(page - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <Button
                      key={i}
                      size="sm"
                      variant={page === i + 1 ? 'default' : 'ghost'}
                      onClick={() => gotoPage(i + 1)}
                      className="h-8 w-8 p-0"
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => gotoPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Filters sidebar */}
          <aside>
            <Card className="border border-border/40 shadow-sm sticky top-20">
              <div className="border-b border-border/40 px-4 py-3">
                <h3 className="text-sm font-semibold">Filters</h3>
              </div>

              <CardContent className="space-y-4 px-4 py-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Job Title</Label>
                  <Select
                    value={jobFilter}
                    onValueChange={setJobFilter}
                    disabled={loading}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All jobs" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt === 'all' ? 'All jobs' : opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Interview Scheduled</Label>
                  <Select
                    value={interviewFilter}
                    onValueChange={setInterviewFilter}
                    disabled={loading}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Resume Score Range</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={resumeMin}
                      onChange={(e) => setResumeMin(e.target.value)}
                      className="h-9"
                      min="0"
                      max="100"
                      disabled={loading}
                    />
                    <span className="text-xs text-muted-foreground">to</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={resumeMax}
                      onChange={(e) => setResumeMax(e.target.value)}
                      className="h-9"
                      min="0"
                      max="100"
                      disabled={loading}
                    />
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={resetFilters}
                  disabled={loading}
                >
                  Reset Filters
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </>
  );
}

function HeaderStat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border/40 bg-card px-3 py-2 shadow-sm">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <div className="flex flex-col">
        <span className="text-base font-semibold leading-none">{value}</span>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      </div>
    </div>
  );
}

export default ApplicationsPage;
